"use client"

import { useState } from "react"

import { useInAdminConsole } from "@/components/admin/theme/admin-ui-provider"
import { useWorkspaceLogo } from "@/hooks/use-workspace-logo"
import { useShellTranslation } from "@/lib/shell/use-shell-translation"

/**
 * The customer's logo in the page header, after the sidebar toggle and before
 * the breadcrumb (owner, 2026-10-28): the workspace's own, else its company's
 * (useWorkspaceLogo). Nothing when there is none, or when it fails to load.
 * Every header that draws <SidebarTrigger> renders this right after the
 * toggle's separator (header-workspace-logo-inventory.guard.spec.ts).
 *
 * The super-admin console shares PageShell and has no tenant: it shows none.
 * The HumanFirewall mark at the top of the sidebar is not this
 * (components/brand/sidebar-brand-mark.tsx).
 */
export function HeaderWorkspaceLogo() {
  const inConsole = useInAdminConsole()
  const logo = useWorkspaceLogo()
  const { t } = useShellTranslation()
  const [failed, setFailed] = useState<string | null>(null)
  if (inConsole || !logo || failed === logo) return null
  // A logo is drawn for a light page: on dark it sits on a white plate.
  return (
    // eslint-disable-next-line @next/next/no-img-element -- a tenant upload or CDN URL, not a static asset
    <img
      data-testid="header-workspace-logo"
      src={logo}
      alt={t("settings.companyLogo.alt")}
      className="hidden h-7 w-auto max-w-32 shrink-0 object-contain sm:block dark:rounded dark:bg-white dark:px-1.5 dark:py-0.5"
      onError={() => setFailed(logo)}
    />
  )
}
